import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { formatCurrency, getUserCurrencySymbol, getUserCountry, COUNTRIES } from '../utils/countries';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Search, Filter, Sparkles, TrendingUp, FileText, MessageSquare, 
  Settings, LogOut, Briefcase, DollarSign, Users, Plus, Edit,
  Loader2, ChevronRight, Building2, Menu, X, Eye, Check, XCircle, Power, Globe, MapPin, Shield,
  BarChart3, CheckCircle2
} from 'lucide-react';

// Verified Badge Component
const VerifiedBadge = ({ size = 'sm', showLabel = false }) => {
  const sizeClasses = {
    xs: 'w-3.5 h-3.5',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className="inline-flex items-center gap-1" title="Verified Creator">
      <div className={`${sizeClasses[size]} rounded-full bg-[#3AAFB9] flex items-center justify-center`}>
        <CheckCircle2 className={`${size === 'xs' ? 'w-2.5 h-2.5' : size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-white`} />
      </div>
      {showLabel && <span className="text-xs font-medium text-[#3AAFB9]">Verified</span>}
    </div>
  );
};

const NICHES = ['Fashion', 'Fitness', 'Tech', 'Beauty', 'Food', 'Travel', 'Lifestyle', 'Gaming', 'Music', 'Education'];
const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'Twitter', 'LinkedIn'];
const FOLLOWER_RANGES = ['Below 1k', '1k-5k', '5k-10k', '10k-25k', '25k-50k', '50k-100k', '100k-250k', '250k-500k', '500k+'];

// Convert COUNTRIES object to array for select dropdowns
const COUNTRIES_LIST = Object.entries(COUNTRIES).map(([code, info]) => ({
  code,
  name: info.name,
  currency: info.currency,
  symbol: info.symbol
}));

const BrandDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [creators, setCreators] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Filters for creators
  const [searchQuery, setSearchQuery] = useState('');
  const [nicheFilter, setNicheFilter] = useState('');
  const [followerFilter, setFollowerFilter] = useState('');

  // AI Matching
  const [aiMatches, setAiMatches] = useState([]);
  const [aiInsights, setAiInsights] = useState('');
  const [matchingLoading, setMatchingLoading] = useState(false);

  // Campaign Dialog
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [campaignData, setCampaignData] = useState({
    title: '',
    description: '',
    niches: [],
    budget_min: 0,
    budget_max: 0,
    requirements: '',
    deadline: '',
    deliverables: [],
    platform_requirements: [],
    country_code: user?.location?.country_code || user?.brand_profile?.country_code || 'US',
    target_countries: [],
    city: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Get user's currency info
  const userCountry = getUserCountry(user);
  const userCurrency = getUserCurrencySymbol(user);
  
  // Get selected country info for currency in campaign creation
  const selectedCountry = COUNTRIES_LIST.find(c => c.code === campaignData.country_code) || COUNTRIES_LIST[0];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, creatorsRes, proposalsRes, statsRes] = await Promise.all([
        api.get('/campaigns/my'),
        api.get('/users/creators'),
        api.get('/proposals/my'),
        api.get('/stats')
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setCreators(creatorsRes.data.creators || []);
      setProposals(proposalsRes.data.proposals || []);
      setStats(statsRes.data || {});
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredCreators = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (nicheFilter) params.append('niche', nicheFilter);
      if (followerFilter) params.append('follower_range', followerFilter);

      const response = await api.get(`/users/creators?${params.toString()}`);
      setCreators(response.data.creators || []);
    } catch (error) {
      toast.error('Failed to search creators');
    }
  };

  const handleAiMatching = async () => {
    setMatchingLoading(true);
    try {
      const response = await api.post('/matching/creators', {
        niches: campaignData.niches.length > 0 ? campaignData.niches : (user?.brand_profile?.industry ? [user.brand_profile.industry] : []),
        budget_range: user?.brand_profile?.budget_range || '',
        brand_requirements: ''
      });
      setAiMatches(response.data.matches || []);
      setAiInsights(response.data.ai_insights || '');
      toast.success('AI matching complete!');
    } catch (error) {
      toast.error('AI matching failed');
    } finally {
      setMatchingLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignData.title || !campaignData.description) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/campaigns/', campaignData);
      toast.success('Campaign created!');
      setShowCampaignDialog(false);
      setCampaignData({
        title: '',
        description: '',
        niches: [],
        budget_min: 0,
        budget_max: 0,
        requirements: '',
        deadline: '',
        deliverables: [],
        platform_requirements: []
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNicheToggle = (niche) => {
    setCampaignData(prev => ({
      ...prev,
      niches: prev.niches.includes(niche)
        ? prev.niches.filter(n => n !== niche)
        : [...prev.niches, niche]
    }));
  };

  const handlePlatformToggle = (platform) => {
    setCampaignData(prev => ({
      ...prev,
      platform_requirements: prev.platform_requirements.includes(platform)
        ? prev.platform_requirements.filter(p => p !== platform)
        : [...prev.platform_requirements, platform]
    }));
  };

  const handleProposalAction = async (proposalId, status) => {
    try {
      await api.put(`/proposals/${proposalId}`, { status });
      toast.success(`Proposal ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update proposal');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const displayCreators = aiMatches.length > 0 ? aiMatches : creators;

  if (loading) {
    return (
      <div className="min-h-screen dashboard-bg flex items-center justify-center">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="glass-card p-8 rounded-2xl">
          <Loader2 className="w-8 h-8 animate-spin text-[#3AAFB9]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dashboard-bg relative overflow-hidden">
      {/* Creative Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Vibrant Orbs */}
        <div className="creator-orb creator-orb-teal" style={{ top: '-10%', right: '10%', opacity: 0.4 }} />
        <div className="creator-orb creator-orb-pink" style={{ bottom: '15%', left: '-5%', opacity: 0.3 }} />
        <div className="creator-orb creator-orb-orange" style={{ top: '50%', right: '-8%', opacity: 0.35 }} />
        
        {/* Network Lines */}
        <div className="network-lines opacity-30" />
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 glass-sidebar z-50 transform transition-transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full relative z-10">
          {/* Logo */}
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_creator-brand-2/artifacts/feyyw8lo_145437-200.png" 
                alt="CmdOut" 
                className="w-10 h-10"
              />
              <div>
                <span className="font-bold text-lg tracking-tight">CmdOut</span>
                <span className="text-[#3AAFB9] font-bold">.io</span>
              </div>
            </div>
            <button onClick={() => setMobileMenuOpen(false)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 mx-4 glass-card rounded-xl mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3AAFB9] to-[#3AAFB9]/70 flex items-center justify-center overflow-hidden shadow-lg shadow-[#3AAFB9]/20">
                {user?.brand_profile?.logo_url ? (
                  <img src={user.brand_profile.logo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate text-gray-900">{user?.brand_profile?.company_name || user?.name}</p>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#3AAFB9] animate-pulse"></div>
                  <p className="text-xs text-[#3AAFB9] font-medium">Brand</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1">
            <NavItem icon={<Briefcase />} label="My Campaigns" active={activeTab === 'campaigns'} onClick={() => setActiveTab('campaigns')} />
            <NavItem icon={<Users />} label="Find Creators" active={activeTab === 'creators'} onClick={() => setActiveTab('creators')} />
            <NavItem icon={<Sparkles />} label="AI Matching" active={activeTab === 'ai'} onClick={() => setActiveTab('ai')} accent="teal" />
            <NavItem icon={<FileText />} label="Proposals" active={activeTab === 'proposals'} onClick={() => setActiveTab('proposals')} />
            <NavItem icon={<Shield />} label="Contracts" onClick={() => navigate('/contracts')} />
            <NavItem icon={<BarChart3 />} label="Analytics" onClick={() => navigate('/brand/analytics')} />
            <NavItem icon={<MessageSquare />} label="Messages" onClick={() => navigate('/brand/messages')} />
            <NavItem icon={<Settings />} label="Settings" onClick={() => navigate('/brand/settings')} />
          </nav>

          {/* Logout */}
          <div className="p-4 mx-4 mb-4">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-gray-500 hover:text-gray-900 hover:bg-red-50 rounded-xl transition-all group"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 group-hover:text-red-500" />
              <span className="group-hover:text-red-500">Log out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen relative z-10">
        {/* Header */}
        <header className="sticky top-0 glass-nav z-40">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {activeTab === 'campaigns' && 'My Campaigns'}
                  {activeTab === 'creators' && 'Find Creators'}
                  {activeTab === 'ai' && 'AI Creator Matching'}
                  {activeTab === 'proposals' && 'Proposals Received'}
                </h1>
                <p className="text-sm text-gray-500">
                  {activeTab === 'campaigns' && 'Manage your active campaigns'}
                  {activeTab === 'creators' && 'Discover talented creators'}
                  {activeTab === 'ai' && 'AI-powered creator recommendations'}
                  {activeTab === 'proposals' && 'Review creator applications'}
                </p>
              </div>
            </div>
            {activeTab === 'campaigns' && (
              <Button 
                onClick={() => setShowCampaignDialog(true)} 
                data-testid="create-campaign-btn"
                className="rounded-xl bg-gradient-to-r from-[#3AAFB9] to-[#2F9AA3] hover:from-[#2F9AA3] hover:to-[#3AAFB9] shadow-lg shadow-[#3AAFB9]/25"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            )}
          </div>
        </header>

        <div className="p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Campaigns" value={stats.total_campaigns || 0} icon={<Briefcase />} />
            <StatCard label="Active" value={stats.active_campaigns || 0} icon={<TrendingUp />} color="teal" />
            <StatCard label="Proposals" value={stats.total_proposals || 0} icon={<FileText />} />
            <StatCard label="Contracts" value={stats.active_contracts || 0} icon={<Users />} color="primary" />
          </div>

          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campaigns.map(campaign => (
                <MyCampaignCard key={campaign.campaign_id} campaign={campaign} onToggleStatus={fetchData} />
              ))}
              {campaigns.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No campaigns yet</p>
                  <Button onClick={() => setShowCampaignDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Campaign
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Find Creators Tab */}
          {activeTab === 'creators' && (
            <div>
              {/* Search & Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Search creators..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchFilteredCreators()}
                    className="pl-10"
                    data-testid="creator-search-input"
                  />
                </div>
                <Select value={nicheFilter || "all"} onValueChange={(v) => setNicheFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-full md:w-40">
                    <SelectValue placeholder="Niche" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Niches</SelectItem>
                    {NICHES.map(niche => (
                      <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={followerFilter || "all"} onValueChange={(v) => setFollowerFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-full md:w-40">
                    <SelectValue placeholder="Followers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sizes</SelectItem>
                    {FOLLOWER_RANGES.map(range => (
                      <SelectItem key={range} value={range}>{range}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={fetchFilteredCreators} variant="outline">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </Button>
              </div>

              {/* Creators Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayCreators.map(creator => (
                  <CreatorCard key={creator.user_id} creator={creator} />
                ))}
                {displayCreators.length === 0 && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    No creators found matching your criteria.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Matching Tab */}
          {activeTab === 'ai' && (
            <div>
              <Card className="mb-6 border-[#3AAFB9]/30 bg-[#3AAFB9]/5">
                <CardContent className="py-6">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-1">AI-Powered Creator Discovery</h3>
                      <p className="text-muted-foreground">
                        Let our AI find the perfect creators for your brand and campaigns.
                      </p>
                    </div>
                    <Button 
                      onClick={handleAiMatching}
                      disabled={matchingLoading}
                      className="bg-[#3AAFB9] hover:bg-[#3AAFB9]/90"
                      data-testid="ai-match-creators-btn"
                    >
                      {matchingLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Find Creators
                    </Button>
                  </div>
                  {aiInsights && (
                    <div className="mt-4 p-4 bg-background/50 rounded-lg">
                      <p className="text-sm">{aiInsights}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aiMatches.map(creator => (
                  <CreatorCard key={creator.user_id} creator={creator} highlight />
                ))}
                {aiMatches.length === 0 && !matchingLoading && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    Click "Find Creators" to get AI-powered recommendations.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Proposals Tab */}
          {activeTab === 'proposals' && (
            <div className="space-y-4">
              {proposals.map(proposal => (
                <ProposalCard 
                  key={proposal.proposal_id} 
                  proposal={proposal}
                  onAccept={() => handleProposalAction(proposal.proposal_id, 'accepted')}
                  onReject={() => handleProposalAction(proposal.proposal_id, 'rejected')}
                />
              ))}
              {proposals.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No proposals yet. Create a campaign to start receiving applications!
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Create Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Location Section */}
            <div className="glass-card rounded-xl p-4 border border-[#3AAFB9]/20">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-[#3AAFB9]" />
                <Label className="font-medium">Campaign Location & Currency</Label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600 mb-2 block">Country *</Label>
                  <Select 
                    value={campaignData.country_code} 
                    onValueChange={(v) => setCampaignData(prev => ({ ...prev, country_code: v }))}
                  >
                    <SelectTrigger data-testid="campaign-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES_LIST.map(country => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name} ({country.symbol})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-gray-600 mb-2 block">City (Optional)</Label>
                  <Input
                    placeholder="e.g., New York"
                    value={campaignData.city}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-sm text-gray-600 mb-2 block">Also show to creators in:</Label>
                <div className="flex flex-wrap gap-2">
                  {COUNTRIES_LIST.filter(c => c.code !== campaignData.country_code).map(country => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => {
                        const current = campaignData.target_countries || [];
                        if (current.includes(country.code)) {
                          setCampaignData(prev => ({ ...prev, target_countries: current.filter(c => c !== country.code) }));
                        } else {
                          setCampaignData(prev => ({ ...prev, target_countries: [...current, country.code] }));
                        }
                      }}
                      className={`px-2 py-1 rounded text-xs transition-all ${
                        (campaignData.target_countries || []).includes(country.code)
                          ? 'bg-[#3AAFB9] text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      }`}
                    >
                      {country.code}
                    </button>
                  ))}
                </div>
              </div>
              {selectedCountry && (
                <p className="text-xs text-[#3AAFB9] mt-3">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Budget will be in <strong>{selectedCountry.currency}</strong> ({selectedCountry.symbol})
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Campaign Title *</Label>
              <Input
                placeholder="e.g., Summer Fashion Collection Launch"
                value={campaignData.title}
                onChange={(e) => setCampaignData(prev => ({ ...prev, title: e.target.value }))}
                data-testid="campaign-title-input"
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Description *</Label>
              <Textarea
                placeholder="Describe your campaign, goals, and what you're looking for..."
                value={campaignData.description}
                onChange={(e) => setCampaignData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                data-testid="campaign-description-input"
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Target Niches</Label>
              <div className="flex flex-wrap gap-2">
                {NICHES.map(niche => (
                  <button
                    key={niche}
                    type="button"
                    onClick={() => handleNicheToggle(niche)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      campaignData.niches.includes(niche)
                        ? 'bg-[#3AAFB9] text-white shadow-lg shadow-[#3AAFB9]/25'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {niche}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Budget Min ({selectedCountry?.symbol || '$'})</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={campaignData.budget_min || ''}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, budget_min: parseFloat(e.target.value) || 0 }))}
                  data-testid="budget-min-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Budget Max ({selectedCountry?.symbol || '$'})</Label>
                <Input
                  type="number"
                  placeholder="500"
                  value={campaignData.budget_max || ''}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, budget_max: parseFloat(e.target.value) || 0 }))}
                  data-testid="budget-max-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-3 block">Platform Requirements</Label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(platform => (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => handlePlatformToggle(platform)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      campaignData.platform_requirements.includes(platform)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Requirements</Label>
              <Textarea
                placeholder="Specific requirements for creators..."
                value={campaignData.requirements}
                onChange={(e) => setCampaignData(prev => ({ ...prev, requirements: e.target.value }))}
                rows={3}
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Deadline</Label>
              <Input
                type="date"
                value={campaignData.deadline}
                onChange={(e) => setCampaignData(prev => ({ ...prev, deadline: e.target.value }))}
              />
            </div>

            <Button 
              onClick={handleCreateCampaign}
              disabled={submitting || !campaignData.title || !campaignData.description}
              className="w-full"
              data-testid="submit-campaign-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Campaign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const NavItem = ({ icon, label, active, onClick, accent }) => (
  <button
    onClick={onClick}
    className={`nav-item flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all ${
      active 
        ? accent === 'teal' 
          ? 'active bg-[#3AAFB9]/10 text-[#3AAFB9] font-medium' 
          : 'active bg-[#3AAFB9]/10 text-[#3AAFB9] font-medium'
        : 'text-gray-600 hover:text-gray-900'
    }`}
    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
  >
    <span className={active ? '' : 'opacity-70'}>{icon}</span>
    <span>{label}</span>
    {active && (
      <div className="ml-auto w-2 h-2 rounded-full bg-[#3AAFB9]" />
    )}
  </button>
);

const StatCard = ({ label, value, icon, color }) => (
  <div className="glass-card stat-card rounded-2xl p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className={`text-2xl font-bold ${color === 'teal' ? 'text-[#3AAFB9]' : color === 'primary' ? 'text-[#3AAFB9]' : 'text-gray-900'}`}>
          {value}
        </p>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
        color === 'teal' ? 'bg-gradient-to-br from-[#3AAFB9] to-[#3AAFB9]/70 text-white shadow-[#3AAFB9]/20' : 
        color === 'primary' ? 'bg-gradient-to-br from-[#3AAFB9] to-[#3AAFB9]/70 text-white shadow-[#3AAFB9]/20' : 
        'bg-gradient-to-br from-gray-100 to-gray-50 text-gray-500'
      }`}>
        {icon}
      </div>
    </div>
  </div>
);

const MyCampaignCard = ({ campaign, onToggleStatus }) => {
  const navigate = useNavigate();
  const [toggling, setToggling] = useState(false);
  
  const handleToggle = async (e) => {
    e.stopPropagation();
    setToggling(true);
    try {
      const newStatus = campaign.status === 'active' ? 'paused' : 'active';
      await api.put(`/campaigns/${campaign.campaign_id}`, { status: newStatus });
      toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'}`);
      if (onToggleStatus) onToggleStatus();
    } catch (error) {
      toast.error('Failed to update campaign status');
    } finally {
      setToggling(false);
    }
  };
  
  return (
    <Card className="card-hover cursor-pointer" onClick={() => navigate(`/campaign/${campaign.campaign_id}`)}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
              {campaign.status === 'active' ? 'Active' : 'Paused'}
            </Badge>
          </div>
          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <span className="text-xs text-muted-foreground">
              {campaign.status === 'active' ? 'Visible' : 'Hidden'}
            </span>
            <Switch
              checked={campaign.status === 'active'}
              onCheckedChange={handleToggle}
              disabled={toggling}
              data-testid={`toggle-campaign-${campaign.campaign_id}`}
            />
          </div>
        </div>
        
        <h3 className="font-semibold text-lg mb-2 line-clamp-2">
          {campaign.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {campaign.description}
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {campaign.niches?.slice(0, 3).map(niche => (
            <Badge key={niche} variant="secondary" className="text-xs">
              {niche}
            </Badge>
          ))}
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-sm">
              <DollarSign className="w-4 h-4 text-[#3AAFB9]" />
              <span>{campaign.currency_symbol || '$'}{campaign.budget_min} - {campaign.currency_symbol || '$'}{campaign.budget_max}</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {campaign.applications_count} applicants
            </span>
          </div>
          <Button size="sm" variant="ghost">
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const CreatorCard = ({ creator, highlight }) => {
  const navigate = useNavigate();
  const profile = creator.creator_profile || {};
  const matchScore = creator.match_score || creator.overall_match_score;
  const analytics = profile.analytics || {};
  const isVerified = profile.verified;
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500 bg-green-500/10';
    if (score >= 60) return 'text-[#3AAFB9] bg-[#3AAFB9]/10';
    if (score >= 40) return 'text-yellow-500 bg-yellow-500/10';
    return 'text-muted-foreground bg-muted';
  };
  
  return (
    <Card className={`card-hover cursor-pointer ${highlight ? 'border-[#3AAFB9]/50' : ''}`} onClick={() => navigate(`/profile/${creator.user_id}`)}>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="relative w-14 h-14 rounded-full bg-secondary overflow-hidden flex-shrink-0">
            {creator.picture ? (
              <img src={creator.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Users className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            {isVerified && (
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#3AAFB9] flex items-center justify-center border-2 border-white">
                <CheckCircle2 className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold truncate">{creator.name}</h3>
              {isVerified && <VerifiedBadge size="xs" />}
              {highlight && (
                <Badge className="bg-[#3AAFB9] text-background text-xs">AI Match</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{profile.follower_range || 'Creator'}</p>
            {/* Match Score Badge */}
            {matchScore && (
              <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${getScoreColor(typeof matchScore === 'object' ? matchScore.overall_score : matchScore)}`}>
                <Sparkles className="w-3 h-3" />
                Match: {typeof matchScore === 'object' ? matchScore.overall_score : matchScore}
              </div>
            )}
          </div>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {profile.bio || 'No bio available'}
        </p>
        
        {/* Analytics Preview */}
        {(analytics.engagement_rate > 0 || analytics.estimated_post_value > 0) && (
          <div className="flex items-center gap-4 mb-4 text-xs">
            {analytics.engagement_rate > 0 && (
              <span className="text-[#E8795A] font-medium">
                {analytics.engagement_rate}% engagement
              </span>
            )}
            {analytics.estimated_post_value > 0 && (
              <span className="text-muted-foreground">
                ~${analytics.estimated_post_value}/post value
              </span>
            )}
          </div>
        )}
        
        <div className="flex flex-wrap gap-1 mb-4">
          {profile.niches?.slice(0, 3).map(niche => (
            <Badge key={niche} variant="secondary" className="text-xs">
              {niche}
            </Badge>
          ))}
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            {profile.platforms?.slice(0, 3).map(platform => (
              <span key={platform} className="text-xs text-muted-foreground">{platform}</span>
            ))}
          </div>
          {profile.rate_per_post > 0 && (
            <span className="text-sm font-medium text-[#3AAFB9]">
              ${profile.rate_per_post}/post
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ProposalCard = ({ proposal, onAccept, onReject }) => {
  const navigate = useNavigate();
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-500',
    accepted: 'bg-green-500/20 text-green-500',
    rejected: 'bg-red-500/20 text-red-500'
  };

  return (
    <Card className="card-hover">
      <CardContent className="py-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-12 h-12 rounded-full bg-secondary overflow-hidden flex-shrink-0">
              {proposal.creator_picture ? (
                <img src={proposal.creator_picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h3 
                  className="font-semibold cursor-pointer hover:text-primary"
                  onClick={() => navigate(`/profile/${proposal.creator_id}`)}
                >
                  {proposal.creator_name}
                </h3>
                <Badge className={statusColors[proposal.status]}>
                  {proposal.status}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-1">
                For: {proposal.campaign_title}
              </p>
              <p className="text-sm text-muted-foreground line-clamp-2">{proposal.pitch}</p>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="text-[#3AAFB9] font-medium">${proposal.proposed_rate}</span>
                {proposal.timeline && (
                  <span className="text-muted-foreground">{proposal.timeline}</span>
                )}
              </div>
            </div>
          </div>
          
          {proposal.status === 'pending' && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onReject} data-testid={`reject-${proposal.proposal_id}`}>
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
              <Button size="sm" onClick={onAccept} className="bg-[#3AAFB9] hover:bg-[#3AAFB9]/90" data-testid={`accept-${proposal.proposal_id}`}>
                <Check className="w-4 h-4 mr-1" />
                Accept
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BrandDashboard;
